const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const safeUrl = (value, fallback = '#') => {
    try {
        const url = new URL(String(value || ''), window.location.origin);

        return ['http:', 'https:'].includes(url.protocol) ? url.toString() : fallback;
    } catch {
        return fallback;
    }
};

const element = (tag, className, text) => {
    const node = document.createElement(tag);

    if (className) {
        node.className = className;
    }

    if (text !== undefined) {
        node.textContent = String(text || '');
    }

    return node;
};

const normalizeOffer = (rawOffer) => {
    const source = rawOffer && typeof rawOffer === 'object' ? rawOffer : {};
    const products = Array.isArray(source.products)
        ? source.products.filter((product) => Number.parseInt(product?.id, 10) > 0)
        : [];

    return {
        ...source,
        enabled: Boolean(source.enabled) && products.length > 0,
        mode: source.mode === 'bogo' ? 'bogo' : 'bestsellers',
        eyebrow: String(source.eyebrow || ''),
        title: String(source.title || ''),
        description: String(source.description || ''),
        promotion: source.promotion && typeof source.promotion === 'object'
            ? source.promotion
            : {},
        products,
    };
};

const variationMatches = (variation, selections, names) => names.every((name) => {
    const expected = String(variation?.attributes?.[name] || '');
    const selected = String(selections[name] || '');

    return selected !== '' && (expected === '' || expected === selected);
});

const optionIsAvailable = (variations, selections, names, activeName, value) => variations.some(
    (variation) => names.every((name) => {
        const expected = String(variation?.attributes?.[name] || '');
        const selected = name === activeName ? value : String(selections[name] || '');

        return selected === '' || expected === '' || expected === selected;
    }),
);

const imageNode = (product) => {
    const link = element('a', 'CartAovProduct__media');
    link.href = safeUrl(product?.url);
    link.setAttribute('aria-label', `Zobacz produkt: ${String(product?.name || 'Produkt')}`);
    const imageData = product?.image || {};

    if (!imageData.src) {
        link.classList.add('is-placeholder');
        return { link, image: null };
    }

    const image = element('img', 'CartAovProduct__image');
    image.src = safeUrl(imageData.src, '');
    image.alt = String(imageData.alt || product?.name || '');
    image.loading = 'eager';
    image.decoding = 'async';
    image.width = Number.parseInt(imageData.width, 10) || 300;
    image.height = Number.parseInt(imageData.height, 10) || 450;

    if (imageData.srcset) {
        image.srcset = String(imageData.srcset);
        image.sizes = '(max-width: 640px) 104px, 280px';
    }

    image.addEventListener('error', () => {
        image.remove();
        link.classList.add('is-placeholder');
    }, { once: true });
    link.append(image);

    return { link, image };
};

const createProduct = (product, { addItem, announce, onConfigure }) => {
    const article = element('article', 'CartAovProduct');
    article.dataset.aovProductId = String(product.id || '');
    const media = imageNode(product);
    article.append(media.link);

    if (product?.promotion?.label) {
        media.link.append(element('span', 'CartAovProduct__promotion', product.promotion.label));
    } else if (product?.bestseller) {
        media.link.append(element('span', 'CartAovProduct__badge', 'Bestseller'));
    }

    const content = element('div', 'CartAovProduct__content');
    content.append(element('p', 'CartAovProduct__category', product.category || 'Produkt'));
    const title = element('a', 'CartAovProduct__title', product.name || 'Produkt');
    title.href = safeUrl(product.url);
    content.append(title);
    const price = element('p', 'CartAovProduct__price', product.price || '');
    content.append(price);
    const pickerPrice = element('p', 'CartAovProduct__pickerPrice', product.price || '');

    const form = element('div', 'CartAovProduct__form');
    const status = element('p', 'CartAovProduct__status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.tabIndex = -1;
    const button = element('button', 'CartAovProduct__add');
    button.type = 'button';
    const buttonLabel = element('span', '', 'Dodaj');
    const buttonArrow = element('span', '', '+');
    buttonArrow.setAttribute('aria-hidden', 'true');
    button.append(buttonLabel, buttonArrow);
    const configure = element('button', 'CartAovProduct__configure');
    configure.type = 'button';
    configure.dataset.aovConfigure = '';
    const configureLabel = element('span');
    const configureIcon = element('span', '', '+');
    configureIcon.setAttribute('aria-hidden', 'true');
    configure.append(configureLabel, configureIcon);

    const variations = Array.isArray(product.variations) ? product.variations : [];
    const attributes = Array.isArray(product.attributes) ? product.attributes : [];
    const controls = [];
    const selections = new Map();
    let matchedVariation = null;
    let isAdding = false;
    let errorMessage = '';
    let sync = () => {};

    attributes.forEach((attribute) => {
        const name = String(attribute?.name || '');
        const label = String(attribute?.label || 'Opcja');
        const options = (Array.isArray(attribute?.options) ? attribute.options : []).filter(
            (option) => String(option?.value || '') !== '',
        );

        if (!name || options.length === 0) {
            return;
        }

        const useChoices = attribute.kind === 'size' && options.length <= 8;
        const group = element('fieldset', 'CartAovProduct__option');
        group.dataset.aovOption = name;
        const header = element('legend', 'CartAovProduct__optionHeader', label);
        const select = useChoices ? null : element('select', 'CartAovProduct__select');
        const choices = useChoices ? element('div', 'CartAovProduct__choices') : null;
        if (select) {
            select.name = name;
            select.setAttribute('aria-label', label);
            const placeholder = element('option', '', `Wybierz ${label.toLowerCase()}`);
            placeholder.value = '';
            select.append(placeholder);
        }
        const control = {
            name,
            group,
            select,
            choices,
            options: [],
        };

        const choose = (value) => {
            if (isAdding || article.dataset.added === 'true') {
                return;
            }

            errorMessage = '';
            if (value) {
                selections.set(name, value);
            } else {
                selections.delete(name);
            }

            normalizeSelections(name);
            sync();
        };

        options.forEach((option) => {
            const value = String(option.value || '');
            const optionLabel = String(option.label || value);
            const optionNode = element(useChoices ? 'button' : 'option', useChoices ? 'CartAovProduct__choice' : '', optionLabel);
            optionNode.value = value;
            if (useChoices) {
                optionNode.type = 'button';
                optionNode.setAttribute('aria-pressed', 'false');
                optionNode.setAttribute('aria-label', `${label}: ${optionLabel}`);
                optionNode.addEventListener('click', () => choose(value));
                choices.append(optionNode);
            } else {
                select.append(optionNode);
            }
            control.options.push(optionNode);
        });
        select?.addEventListener('change', () => choose(select.value));

        const requestedDefault = String(attribute?.selected || '');
        const defaultValue = options.some((option) => String(option.value || '') === requestedDefault)
            ? requestedDefault
            : (options.length === 1 ? String(options[0].value || '') : '');

        if (defaultValue) {
            selections.set(name, defaultValue);
        }

        group.append(header, choices || select);
        form.append(group);
        controls.push(control);
    });

    const names = controls.map((control) => control.name);
    const needsConfiguration = product.is_variable && controls.some((control) => control.options.length > 1);
    const sizeOnly = attributes.length === 1 && attributes[0]?.kind === 'size';
    const configureText = sizeOnly ? 'Wybierz rozmiar' : 'Wybierz opcje';
    const setPickerOpen = (open, restoreFocus = true) => {
        if (!needsConfiguration) return;
        if (open) onConfigure?.(() => setPickerOpen(false, false));
        article.classList.toggle('is-configuring', open);
        configure.setAttribute('aria-expanded', String(open));
        form.hidden = !open;
        content.inert = open;
        if (open) {
            button.before(status);
            const firstControl = controls.find((control) => !control.group.hidden);
            (firstControl?.select || firstControl?.options.find((option) => !option.disabled))?.focus({ preventScroll: true });
            window.requestAnimationFrame(() => {
                if (form.hidden) return;
                const scroller = article.closest('.CartAovModal__body');
                if (!scroller) return;
                const viewport = scroller.getBoundingClientRect();
                const picker = form.getBoundingClientRect();
                const offset = picker.height > viewport.height || picker.top < viewport.top
                    ? picker.top - viewport.top
                    : Math.max(0, picker.bottom - viewport.bottom + 16);
                if (offset) scroller.scrollBy({
                    top: offset,
                    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
                });
            });
        } else {
            content.append(status);
            if (restoreFocus) configure.focus({ preventScroll: true });
        }
    };

    controls.forEach((control) => {
        control.group.hidden = control.options.length === 1;
    });

    if (needsConfiguration) {
        form.classList.add('CartAovProduct__picker');
        form.id = `aov-options-${product.id}`;
        form.hidden = true;
        configure.setAttribute('aria-controls', form.id);
        configure.setAttribute('aria-expanded', 'false');
        const pickerHeader = element('div', 'CartAovProduct__pickerHeader');
        const pickerCopy = element('div');
        pickerCopy.append(element('p', 'CartAovProduct__pickerTitle', product.name), pickerPrice);
        pickerHeader.append(pickerCopy);
        const dismiss = element('button', 'CartAovProduct__dismiss');
        dismiss.type = 'button';
        dismiss.setAttribute('aria-label', `Zamknij wybór opcji: ${product.name}`);
        dismiss.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6"/></svg>';
        dismiss.addEventListener('click', () => setPickerOpen(false));
        pickerHeader.append(dismiss);
        form.prepend(pickerHeader);
        configure.addEventListener('click', () => setPickerOpen(true));
    } else if (controls.length > 0) {
        const note = controls.map((control) => control.options[0]?.textContent).filter(Boolean).join(' · ');
        content.append(element('p', 'CartAovProduct__variantNote', note));
    }

    const getSelections = () => Object.fromEntries(names.map((name) => [
        name,
        String(selections.get(name) || ''),
    ]));
    const normalizeSelections = (preservedName = '') => {
        let changed = true;

        while (changed) {
            changed = false;

            for (const control of controls) {
                if (control.name === preservedName) {
                    continue;
                }

                const selected = String(selections.get(control.name) || '');

                if (!selected) {
                    continue;
                }

                if (!optionIsAvailable(
                    variations,
                    getSelections(),
                    names,
                    control.name,
                    selected,
                )) {
                    selections.delete(control.name);
                    changed = true;
                }
            }
        }
    };

    sync = () => {
        const currentSelections = getSelections();

        controls.forEach((control) => {
            control.options.forEach((optionNode) => {
                const available = optionIsAvailable(
                    variations,
                    currentSelections,
                    names,
                    control.name,
                    optionNode.value,
                );
                optionNode.disabled = !available || (Boolean(control.choices) && (isAdding || article.dataset.added === 'true'));
                if (control.choices) {
                    optionNode.setAttribute('aria-pressed', String(currentSelections[control.name] === optionNode.value));
                }
            });
            if (control.select) {
                control.select.value = String(selections.get(control.name) || '');
                control.select.disabled = isAdding || article.dataset.added === 'true';
            }
        });

        matchedVariation = variations.find((variation) => (
            variationMatches(variation, currentSelections, names)
        )) || null;

        if (matchedVariation?.price) {
            price.textContent = String(matchedVariation.price);
        } else {
            price.textContent = String(product.price || '');
        }
        pickerPrice.textContent = price.textContent;

        if (media.image && matchedVariation?.image?.src) {
            media.image.src = safeUrl(matchedVariation.image.src, media.image.src);
            media.image.srcset = String(matchedVariation.image.srcset || '');
            media.image.alt = String(matchedVariation.image.alt || product.name || '');
        }

        const ready = !product.is_variable || Boolean(matchedVariation);
        button.disabled = isAdding || !ready || article.dataset.added === 'true';
        buttonLabel.textContent = article.dataset.added === 'true'
            ? 'Dodano'
            : (ready ? 'Dodaj' : (sizeOnly ? 'Wybierz rozmiar' : 'Wybierz opcje'));
        buttonArrow.textContent = article.dataset.added === 'true' ? '✓' : (ready ? '+' : '');
        button.setAttribute('aria-label', `${buttonLabel.textContent}: ${product.name || 'Produkt'}`);
        configureLabel.textContent = article.dataset.added === 'true' ? 'Dodano do koszyka' : configureText;
        configureIcon.textContent = article.dataset.added === 'true' ? '✓' : '+';
        configure.disabled = article.dataset.added === 'true';
        configure.setAttribute('aria-label', `${configureLabel.textContent}: ${product.name || 'Produkt'}`);

        if (product.is_variable && names.every((name) => currentSelections[name]) && !matchedVariation) {
            status.textContent = 'Ta kombinacja jest obecnie niedostępna.';
        } else if (article.dataset.added !== 'true') {
            status.textContent = errorMessage;
        }
    };

    normalizeSelections();
    sync();

    button.addEventListener('click', async () => {
        if (isAdding || button.disabled || typeof addItem !== 'function') {
            return;
        }

        const selections = getSelections();
        const requestBody = {
            product_id: Number.parseInt(product.id, 10),
            variation_id: Number.parseInt(matchedVariation?.variation_id || '0', 10),
            quantity: 1,
            variation: product.is_variable ? selections : {},
        };
        isAdding = true;
        errorMessage = '';
        button.disabled = true;
        controls.forEach((control) => {
            if (control.select) control.select.disabled = true;
            if (control.choices) control.options.forEach((option) => { option.disabled = true; });
        });
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        buttonLabel.textContent = 'Dodawanie…';
        status.textContent = '';

        try {
            const payload = await addItem(requestBody);
            const addedKey = String(payload?.added?.key || '');
            const addedItem = Array.isArray(payload?.items)
                ? payload.items.find((item) => String(item?.key || '') === addedKey)
                : null;
            const appliedBogo = String(addedItem?.bogo_offer?.label || '');
            article.dataset.added = 'true';
            article.classList.add('is-added');
            status.textContent = appliedBogo
                ? `Dodano. ${appliedBogo}.`
                : 'Produkt dodano do koszyka.';
            if (needsConfiguration) {
                setPickerOpen(false, false);
                status.focus({ preventScroll: true });
            }
            announce(status.textContent);
        } catch (requestError) {
            errorMessage = requestError?.message || 'Nie udało się dodać produktu.';
            status.textContent = errorMessage;
            announce(status.textContent);
        } finally {
            isAdding = false;
            button.classList.remove('is-loading');
            button.removeAttribute('aria-busy');
            sync();
        }
    });

    form.append(status, button);
    if (needsConfiguration) {
        const action = element('div', 'CartAovProduct__action');
        action.append(configure);
        content.append(action);
    } else {
        content.append(form);
    }
    article.append(content);
    if (needsConfiguration) article.append(form);

    return article;
};

export const initCartAov = (header, { beforeOpen, addItem, openCart } = {}) => {
    const modal = header.querySelector('[data-cart-aov]');
    const surface = modal?.querySelector('[data-aov-surface]');

    if (!modal || !surface || modal.dataset.aovReady === 'true') {
        return null;
    }

    const closeButtons = [...modal.querySelectorAll('[data-aov-close]')];
    const eyebrow = modal.querySelector('[data-aov-eyebrow]');
    const title = modal.querySelector('[data-aov-title]');
    const description = modal.querySelector('[data-aov-description]');
    const promotion = modal.querySelector('[data-aov-promotion]');
    const promotionLabel = modal.querySelector('[data-aov-promotion-label]');
    const products = modal.querySelector('[data-aov-products]');
    const added = modal.querySelector('[data-aov-added]');
    const shipping = modal.querySelector('[data-aov-shipping]');
    const shippingMessage = modal.querySelector('[data-aov-shipping-message]');
    const shippingCaption = modal.querySelector('[data-aov-shipping-caption]');
    const shippingProgress = modal.querySelector('[data-aov-shipping-progress]');
    const cartCount = modal.querySelector('[data-aov-cart-count]');
    const cartSummary = modal.querySelector('[data-aov-cart-summary]');
    const cartTotal = modal.querySelector('[data-aov-cart-total]');
    const cartButton = modal.querySelector('[data-aov-cart]');
    const live = modal.querySelector('[data-aov-live]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let returnFocus = null;
    let closeTimer = 0;
    let focusTimer = 0;
    let openFrame = 0;
    let closeActivePicker = null;

    const updateCartDetails = (cart) => {
        const progress = cart?.shipping_progress;
        shipping.hidden = !progress?.enabled || cart?.is_empty === true;
        shipping.classList.toggle('is-complete', Boolean(progress?.complete));
        shippingMessage.textContent = String(progress?.message || '');
        shippingCaption.textContent = progress?.complete
            ? 'Dostawa gratis do Twojego zamówienia'
            : String(progress?.caption || '');
        shippingProgress.style.transform = `scaleX(${Math.max(0, Math.min(100, Number(progress?.percent) || 0)) / 100})`;
        cartCount.hidden = !cart?.count;
        cartCount.textContent = `(${Number(cart?.count) || 0})`;
        cartTotal.textContent = String(cart?.summary?.total || '');
        cartSummary.hidden = !cart?.summary?.total;
    };

    const showAddedProduct = (cart) => {
        const item = cart?.items?.find((entry) => entry.key === cart?.added?.key);
        const copy = element('div', 'CartAovModal__addedCopy');
        copy.append(element('p', 'CartAovModal__addedName', item?.name || 'Twój wybór jest już w koszyku.'));
        added.replaceChildren();
        if (item) {
            const media = imageNode(item).link;
            media.className = 'CartAovModal__addedMedia';
            const image = media.querySelector('img');
            if (image) image.sizes = '(max-width: 640px) 48px, 56px';
            added.append(media);
            copy.append(element('p', 'CartAovModal__addedDetails', [
                item.variation,
                `${cart.added.quantity || 1} szt.`,
            ].filter(Boolean).join(' · ')));
        }
        added.append(copy);
    };

    const announce = (message) => {
        if (!live) {
            return;
        }

        live.textContent = '';
        window.setTimeout(() => {
            live.textContent = String(message || '');
        }, 20);
    };

    const setInert = (inert) => {
        modal.inert = inert;
        modal.toggleAttribute('inert', inert);
    };

    const isOpen = () => modal.classList.contains('is-open');

    const releaseScroll = () => {
        document.body.classList.remove('has-cart-aov-open');
        document.body.style.removeProperty('--seluvia-aov-scrollbar-width');
    };

    const close = ({ restoreFocus = true, immediate = false } = {}) => {
        if (!isOpen() && modal.getAttribute('aria-hidden') === 'true') {
            if (immediate) {
                window.clearTimeout(closeTimer);
                releaseScroll();
            }
            return;
        }

        window.clearTimeout(closeTimer);
        window.clearTimeout(focusTimer);
        window.cancelAnimationFrame(openFrame);
        closeActivePicker?.();
        closeActivePicker = null;
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');

        if (restoreFocus && returnFocus instanceof HTMLElement) {
            returnFocus.focus({ preventScroll: true });
        }

        returnFocus = null;
        // Do not allow Tab into a dialog while its closing transition runs.
        setInert(true);
        if (immediate || reducedMotion.matches) {
            releaseScroll();
        } else {
            closeTimer = window.setTimeout(releaseScroll, 240);
        }
    };

    const open = (rawOffer, { trigger = null, cart = null } = {}) => {
        const offer = normalizeOffer(rawOffer);

        if (!offer.enabled) {
            return false;
        }

        beforeOpen?.();
        window.clearTimeout(closeTimer);
        window.clearTimeout(focusTimer);
        returnFocus = trigger instanceof HTMLElement
            ? trigger
            : (document.activeElement instanceof HTMLElement ? document.activeElement : null);
        modal.dataset.mode = offer.mode;
        modal.dataset.productCount = String(offer.products.length);
        eyebrow.textContent = offer.mode === 'bogo' ? 'Oferta dla Ciebie' : 'Polecane';
        title.textContent = offer.title;
        description.textContent = offer.description;
        const hasPromotion = offer.mode === 'bogo' && (offer.promotion?.active || Number(offer.promotion?.percentage) > 0);
        promotion.hidden = !hasPromotion;
        promotionLabel.textContent = hasPromotion ? 'Rabat naliczy się w koszyku' : '';
        closeActivePicker = null;
        products.replaceChildren(...offer.products.map((product) => {
            const card = createProduct(product, {
                addItem: async (body) => {
                    const payload = await addItem(body);
                    updateCartDetails(payload);
                    return payload;
                },
                announce,
                onConfigure: (closePicker) => {
                    closeActivePicker?.();
                    closeActivePicker = closePicker;
                },
            });
            return card;
        }));
        showAddedProduct(cart);
        updateCartDetails(cart);
        modal.querySelector('.CartAovModal__body').scrollTop = 0;
        products.scrollLeft = 0;
        setInert(false);
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.setProperty(
            '--seluvia-aov-scrollbar-width',
            `${Math.max(0, window.innerWidth - document.documentElement.clientWidth)}px`,
        );
        document.body.classList.add('has-cart-aov-open');
        openFrame = window.requestAnimationFrame(() => {
            modal.classList.add('is-open');
            focusTimer = window.setTimeout(
                () => modal.querySelector('.CartAovModal__success')?.focus({ preventScroll: true }),
                reducedMotion.matches ? 0 : 80,
            );
        });
        announce(offer.mode === 'bogo'
            ? `${offer.title}. Wybierz produkt objęty rabatem.`
            : 'Produkt dodano do koszyka. Pokazujemy polecane bestsellery.');

        return true;
    };

    closeButtons.forEach((button) => button.addEventListener('click', () => close()));
    cartButton?.addEventListener('click', () => {
        close({ restoreFocus: false, immediate: true });
        openCart?.();
    });

    document.addEventListener('keydown', (event) => {
        if (!isOpen()) {
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const focusable = [...surface.querySelectorAll(focusableSelector)].filter(
            (node) => !node.closest('[inert]') && node.getClientRects().length > 0,
        );
        const first = focusable[0];
        const last = focusable.at(-1);

        if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
            event.preventDefault();
            last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !surface.contains(document.activeElement))) {
            event.preventDefault();
            first?.focus();
        }
    });

    window.addEventListener('pagehide', () => close({ restoreFocus: false, immediate: true }));
    setInert(true);
    modal.dataset.aovReady = 'true';

    return { open, close, isOpen };
};
